import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import { CardActionArea, CardMedia, styled } from "@mui/material";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import FileCardContent from "components/FileCardContent";
import FileTabCard from "components/FileTabCard";
import { DocumentWebLinkInputType, DocumentWebLinkType } from "operations/schema/schema";
import { FC, useCallback } from "react";
import { useIntl } from "react-intl";

const PREFIX = "LinkPreview";

const classes = {
  cover: `${PREFIX}-cover`,
};

const StyledCardMedia = styled(CardMedia)(() => ({
  [`&.${classes.cover}`]: {
    minWidth: 120,
    minHeight: 120,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

export interface LinkPreviewProps {
  link: DocumentWebLinkInputType | DocumentWebLinkType;
  onDelete?: () => void;
  onEdit?: () => void;
  readonly?: boolean;
}

export const LinkPreview: FC<LinkPreviewProps> = (props) => {
  const { link, onDelete, onEdit, readonly } = props;
  const intl = useIntl();

  const handleDelete = useCallback(
    (event: any) => {
      if (!onDelete) return;
      event.preventDefault();
      onDelete();
    },
    [onDelete],
  );

  const openLink = useCallback(() => {
    window.open(link.link, "_blank", "noopener noreferrer");
  }, [link.link]);

  return (
    <Grid item xs={12} width="100%">
      <FileTabCard key={link.name} data-testid={`LinkPreview-FileTabCard-${link.name}`}>
        <CardActionArea
          onClick={openLink}
          sx={{
            cursor: "pointer",
            display: "flex",
            ".MuiCardActionArea-focusHighlight": {
              background: "transparent",
            },
          }}
          disableRipple
          disableTouchRipple
          style={{ width: "auto" }}
        >
          <StyledCardMedia className={classes.cover} title={link.name}>
            <LinkIcon fontSize="large" color="info" />
          </StyledCardMedia>
        </CardActionArea>
        <FileCardContent>
          <CardActionArea
            onClick={openLink}
            sx={{
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
              flexGrow: 1,
              ".MuiCardActionArea-focusHighlight": {
                background: "transparent",
              },
            }}
            disableRipple
            disableTouchRipple
          >
            <Typography
              component="p"
              variant="body2"
              sx={{
                wordWrap: "break-word",
              }}
            >
              {link.name}
            </Typography>
            <Typography
              component="p"
              variant="body2"
              sx={{
                wordWrap: "break-word",
              }}
            >
              {link.link}
            </Typography>
          </CardActionArea>
        </FileCardContent>
        {!readonly && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: "column",
            }}
          >
            {onDelete && (
              <IconButton
                aria-label={intl.formatMessage({ id: "general.delete" })}
                onClick={handleDelete}
                data-testid="LinkPreview-DeleteButton"
                size="large"
              >
                <DeleteIcon color="error" />
              </IconButton>
            )}
            {onEdit && !readonly && (
              <IconButton
                aria-label={intl.formatMessage({ id: "general.edit" })}
                onClick={onEdit}
                data-testid="LinkPreview-EditButton"
                size="large"
              >
                <EditIcon color="action" />
              </IconButton>
            )}
          </div>
        )}
      </FileTabCard>
    </Grid>
  );
};
